/**
 * Composants de contenu : onglets, listes, minuteur, avatar de ville.
 */

import { formatClock, splitDuration } from '@/core/format';
import { icon } from '../icons';
import type { IconName } from '../icons';
import { DOMAIN, LEAGUE } from '../tokens';
import type { DomainName } from '../tokens';
import type { Component } from './index';

/* ── Onglets ────────────────────────────────────────────────────────────── */

export interface TabDefinition {
  readonly id: string;
  readonly label: string;
  readonly icon?: IconName;
}

export interface TabsOptions {
  readonly tabs: readonly TabDefinition[];
  readonly active?: string;
  readonly onChange?: (id: string) => void;
}

export interface TabsHandle extends Component {
  setActive(id: string): void;
  getActive(): string;
  /**
   * Pastille de notification sur un onglet. 0 l'efface.
   *
   * ⚠️ Elle existe pour la même raison que celle du dock : « une récompense qui attend
   * dans un panneau fermé n'existe pas ». Un onglet fermé est exactement ça, d'un cran
   * plus bas — le dock disait « il y a quelque chose dans Progression », et le joueur
   * ouvrait trois onglets pour trouver quoi (demande de Benoit, 21 août).
   */
  setBadge(id: string, count: number): void;
}

export function createTabs(options: TabsOptions): TabsHandle {
  const el = document.createElement('div');
  el.className = 'v-tabs';
  el.setAttribute('role', 'tablist');

  const first = options.tabs[0];
  let active = options.active ?? first?.id ?? '';
  const buttons = new Map<string, HTMLButtonElement>();
  const badges = new Map<string, HTMLSpanElement>();

  const setActive = (id: string): void => {
    if (!buttons.has(id)) return;
    active = id;
    for (const [key, button] of buttons) {
      button.setAttribute('aria-selected', String(key === id));
    }
    options.onChange?.(id);
  };

  for (const tab of options.tabs) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'v-tabs__tab';
    /*
      ⚠️ CHAQUE ONGLET PORTE SON NOM, pas seulement sa place.

      Quatre sondes désignaient un onglet par sa position — `.v-tabs__tab').nth(2)` —
      dont `decor.probe`, juste sous un commentaire qui énonce « on nomme ce qu'on vise ».
      L'ajout de l'onglet Classement (0.98.0) a décalé « Ères » d'un cran : la sonde a
      cliqué sur le classement, puis attendu trente secondes un bouton de prestige
      invisible, et échoué sur un symptôme muet à vingt lignes de sa cause.

      C'est la règle 22 exactement : un sélecteur positionnel est une supposition sur
      l'ordre du DOM, et cet ordre change dès qu'on ajoute une entrée. Un `data-tab` ne
      change pas.
    */
    button.dataset.tab = tab.id;
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-selected', String(tab.id === active));
    /*
      L'identité de l'onglet, lisible depuis le DOM.

      Sans elle, une sonde ne peut désigner un onglet que par sa POSITION — exactement
      la supposition que la règle 22 interdit, et qui a déjà coûté une sonde muette le
      jour où un sélecteur de quantité s'est glissé devant « Poser ». Un onglet ajouté
      en tête ne doit renommer aucun autre.
    */
    button.dataset.tab = tab.id;

    if (tab.icon !== undefined) {
      button.append(icon(tab.icon));
    }
    const label = document.createElement('span');
    label.textContent = tab.label;
    button.append(label);

    button.addEventListener('click', () => {
      setActive(tab.id);
    });

    /*
      La pastille est créée MASQUÉE et vit dans le bouton : la placer au moment où le
      compte arrive obligerait à connaître l'ordre des enfants, et le libellé s'est déjà
      glissé devant une icône une fois (règle 22).
    */
    const badge = document.createElement('span');
    badge.className = 'v-tabs__badge';
    badge.hidden = true;
    button.append(badge);
    badges.set(tab.id, badge);

    buttons.set(tab.id, button);
    el.append(button);
  }

  return {
    el,
    setActive,
    getActive: () => active,
    setBadge(id, count) {
      const badge = badges.get(id);
      if (badge === undefined) return;
      badge.hidden = count <= 0;
      badge.textContent = count > 9 ? '9+' : String(count);
    },
  };
}

/* ── Liste ──────────────────────────────────────────────────────────────── */

export interface ListRow {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: IconName;
  readonly domain?: DomainName;
  /** Valeur alignée à droite ; en chiffres tabulaires si elle en contient. */
  readonly value?: string;
  readonly onClick?: () => void;
}

export function createList(rows: readonly ListRow[]): Component {
  const el = document.createElement('div');
  el.className = 'v-list';

  for (const row of rows) {
    const item = document.createElement(row.onClick === undefined ? 'div' : 'button');
    item.className = 'v-list__row';
    if (item instanceof HTMLButtonElement) {
      item.type = 'button';
      item.addEventListener('click', row.onClick ?? (() => undefined));
    }

    if (row.icon !== undefined) {
      const tone = row.domain === undefined ? undefined : DOMAIN[row.domain].base;
      item.append(icon(row.icon, tone === undefined ? {} : { color: tone }));
    }

    const text = document.createElement('span');
    text.className = 'v-list__text';
    const title = document.createElement('b');
    title.textContent = row.title;
    text.append(title);

    if (row.subtitle !== undefined) {
      const subtitle = document.createElement('small');
      subtitle.textContent = row.subtitle;
      text.append(subtitle);
    }
    item.append(text);

    if (row.value !== undefined) {
      const value = document.createElement('span');
      value.className = 'v-list__value';
      value.textContent = row.value;
      item.append(value);
    }

    el.append(item);
  }

  return { el };
}

/* ── Minuteur ───────────────────────────────────────────────────────────── */

export interface TimerOptions {
  /** Instant de fin, en millisecondes epoch. */
  readonly endsAt: number;
  readonly domain?: DomainName;
  readonly onEnd?: () => void;
}

export interface TimerHandle extends Component {
  /** Rafraîchit l'affichage pour un instant donné. Sans argument : maintenant. */
  tick(now?: number): void;
  stop(): void;
}

/**
 * Décompte lisible d'un coup d'œil. Le rendu passe par `formatClock`, neutre
 * linguistiquement : un minuteur s'écrit pareil dans toutes les langues.
 *
 * Il se rafraîchit une fois par seconde, pas à chaque frame — un texte qui change
 * 60 fois par seconde coûte des recalculs de mise en page pour rien.
 */
export function createTimer(options: TimerOptions): TimerHandle {
  const el = document.createElement('span');
  el.className = 'v-timer';

  const tone = DOMAIN[options.domain ?? 'social'];
  el.style.color = 'var(--text-on-night)';
  el.style.background = `color-mix(in srgb, ${tone.base} 15%, transparent)`;
  el.style.borderColor = `color-mix(in srgb, ${tone.base} 32%, transparent)`;

  let ended = false;
  let interval = 0;

  const tick = (now: number = Date.now()): void => {
    const remaining = Math.max(0, options.endsAt - now);
    el.textContent = formatClock(remaining);

    if (remaining === 0 && !ended) {
      ended = true;
      el.classList.add('is-ended');
      options.onEnd?.();
      window.clearInterval(interval);
    }
  };

  tick();
  // `window` et non `globalThis` : avec les types Node chargés, `globalThis.setInterval`
  // rend un `Timeout` et non un identifiant numérique.
  interval = window.setInterval(() => {
    tick();
  }, 1000);

  return {
    el,
    tick,
    stop() {
      window.clearInterval(interval);
    },
  };
}

/* ── Avatar de ville ────────────────────────────────────────────────────── */

export interface AvatarTraits {
  readonly hue: number;
  readonly silhouette: readonly number[];
  readonly league: keyof typeof LEAGUE;
}

const LEAGUE_ORDER = Object.keys(LEAGUE) as (keyof typeof LEAGUE)[];

/**
 * Traits déterministes d'une ville, dérivés de son slug.
 *
 * Deux joueurs ne doivent jamais voir le même emblème pour la même ville, et la même
 * ville doit rendre le même emblème partout — dans le classement, sur la page publique,
 * dans le panneau de jumelage. D'où une dérivation pure, sans stockage ni aléa.
 */
export function avatarTraits(slug: string, prestige = 0): AvatarTraits {
  let hash = 2166136261;
  for (let i = 0; i < slug.length; i++) {
    hash ^= slug.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const seed = Math.abs(hash);

  const silhouette = [0, 1, 2, 3, 4].map((i) => 3 + ((seed >>> (i * 3)) % 6));
  const leagueIndex = Math.min(LEAGUE_ORDER.length - 1, Math.max(0, prestige));

  return {
    hue: seed % 360,
    silhouette,
    league: LEAGUE_ORDER[leagueIndex] ?? 'wood',
  };
}

export interface AvatarOptions {
  readonly slug: string;
  readonly prestige?: number;
  readonly size?: number;
}

export function createCityAvatar(options: AvatarOptions): Component {
  const size = options.size ?? 56;
  const traits = avatarTraits(options.slug, options.prestige ?? 0);

  const el = document.createElement('span');
  el.className = 'v-avatar';
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.background = `linear-gradient(160deg, hsl(${traits.hue} 46% 34%), hsl(${(traits.hue + 40) % 360} 40% 20%))`;
  el.style.borderColor = LEAGUE[traits.league];
  el.setAttribute('role', 'img');
  el.setAttribute('aria-label', options.slug);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.classList.add('v-avatar__skyline');

  traits.silhouette.forEach((height, index) => {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(1.5 + index * 3.5));
    rect.setAttribute('y', String(16 - height));
    rect.setAttribute('width', '2.6');
    rect.setAttribute('height', String(height));
    rect.setAttribute('rx', '0.6');
    rect.setAttribute('fill', 'currentColor');
    rect.setAttribute('opacity', index % 2 === 0 ? '0.95' : '0.72');
    svg.append(rect);
  });

  el.append(svg);
  return { el };
}

export { splitDuration };
